import React, { useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Lightweight in-chat mentor-style switcher. Lets the user change the
 * mentor's tone on the fly. Persists the new style to the onboarding
 * document via PATCH /api/onboarding so it survives across sessions.
 *
 * The 5 labels here map 1:1 to canonical backend modes via
 * LEGACY_STYLE_MAP in server.py — backend handles the translation.
 */

const STYLES = [
  {
    key: "gentle_friend",
    label: "Gentle Friend",
    blurb: "Warm, comforting, patient.",
  },
  {
    key: "direct_coach",
    label: "Direct Coach",
    blurb: "Clear, honest, action-focused.",
  },
  {
    key: "strategic_mentor",
    label: "Strategic Mentor",
    blurb: "Organized, analytical, goal-focused.",
  },
  {
    key: "spiritual_encourager",
    label: "Spiritual Encourager",
    blurb: "Hopeful, purpose-driven, faith-friendly.",
  },
  {
    key: "accountability_coach",
    label: "Accountability Coach",
    blurb: "Firm, supportive, follow-through-focused.",
  },
];

// Map legacy backend keys to the display style keys above
const BACK_MAP = {
  calm: "gentle_friend",
  gentle: "gentle_friend",
  hard: "direct_coach",
  nobs: "direct_coach",
  direct: "direct_coach",
  friend: "gentle_friend",
  motivational: "direct_coach",
  tools: "strategic_mentor",
  strategic: "strategic_mentor",
  explore: "spiritual_encourager",
  reflective: "spiritual_encourager",
  accountability: "accountability_coach",
  gentle_friend: "gentle_friend",
  direct_coach: "direct_coach",
  strategic_mentor: "strategic_mentor",
  spiritual_encourager: "spiritual_encourager",
  accountability_coach: "accountability_coach",
};

export function MentorStyleSwitcher({ currentStyle, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const activeKey = BACK_MAP[currentStyle] || "gentle_friend";
  const active = STYLES.find((s) => s.key === activeKey) || STYLES[0];

  const pick = async (styleKey) => {
    setSaving(true);
    try {
      // Persist to onboarding so it survives reloads and new sessions.
      await axios.patch(`${API}/onboarding`, { coach_style: styleKey });
      onChange?.(styleKey);
      const label = STYLES.find((s) => s.key === styleKey)?.label || styleKey;
      toast.success(`Mentor style: ${label}`);
    } catch (e) {
      toast.error("Couldn't save style — try again.");
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative inline-block" data-testid="mentor-style-switcher">
      <button
        data-testid="mentor-style-toggle"
        aria-label="Change mentor style"
        onClick={() => setOpen((o) => !o)}
        disabled={saving}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#e6dcc6] hover:border-[#486a8f]/40 transition-colors ${
          compact ? "text-[11px]" : "text-[12px]"
        } font-mono-eyebrow text-[#486a8f]`}
      >
        <span className="hidden sm:inline">Mentor:</span>
        <span className="font-medium">{active.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          {/* Backdrop tap-to-close */}
          <button
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default bg-transparent"
          />
          <div
            data-testid="mentor-style-menu"
            className="absolute right-0 mt-2 w-72 rounded-2xl bg-white border border-[#eadfc9] shadow-lg z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-[#f2ecdc]">
              <p className="font-mono-eyebrow text-[10px] text-[#486a8f] mb-1">CHANGE STYLE</p>
              <p className="font-body text-[12px] text-[#4b5563] leading-relaxed">
                Your mentor will shift tone immediately and remember it next time.
              </p>
            </div>
            <div className="py-1">
              {STYLES.map((s) => {
                const isActive = s.key === activeKey;
                return (
                  <button
                    key={s.key}
                    data-testid={`mentor-style-option-${s.key}`}
                    onClick={() => pick(s.key)}
                    disabled={saving}
                    className={`w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-[#faf6ec] transition-colors ${
                      isActive ? "bg-[#faf6ec]" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-[13px] font-medium text-[#0f1724]">
                        {s.label}
                      </p>
                      <p className="font-body text-[11px] text-[#6b7280] mt-0.5 leading-snug">
                        {s.blurb}
                      </p>
                    </div>
                    {isActive && (
                      <Check size={14} className="text-[#3e6b52] mt-0.5 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MentorStyleSwitcher;
